<template>
  <section
    :id="data.id"
    v-lazyBackground="'/images/game/gameSliderBackground.jpg'"
    class="content-visibility relative sm:mx-auto pl-4 sm:px-8 pb-6 pt-6 sm:pt-0 rounded-none sm:rounded-lg"
    :class="isMobile ? 'min-h-[350px]' : 'min-h-[345px]'"
  >
    <OImage v-if="!isMobile" loading="lazy" src="/images/game/coin.png" class="w-[42px] h-[40px] absolute -left-5 bottom-4" />
    <OImage v-if="!isMobile" loading="lazy" src="/images/game/heart.png" class="w-[42px] h-[38px] absolute left-[306px] -top-3.5" />
    <OImage v-if="!isMobile" loading="lazy" src="/images/game/cup.png" class="w-[32px] h-[28px] absolute right-[-18px] bottom-3" />
    <OImage v-if="!isMobile" loading="lazy" src="/images/game/joystick.png" class="w-[53px] h-[42px] absolute -right-5 top-16" />
    <div class="w-full relative flex items-center" :class="IS_ONEDIO ? 'mb-0' : 'mb-8 pt-6'">
      <div class="flex items-center relative w-auto" :style="{ color: constantData.style.headerTextColor }">
        <OIcon :icon="`category/${constantData.style.icon}`" svg-class="w-8 h-8" />
        <div class="block font-semibold leading-18 text-[21px] pl-1 flex-shrink-0 mr-1">{{ $t('game.games') }}</div>
      </div>
      <div class="bg-white opacity-20 w-full h-px mx-2 grow"></div>
      <OImage v-if="IS_ONEDIO && !isMobile" loading="lazy" src="/images/game/oyunBasliyor.png" class="h-[126px] w-[293px]" />

      <div v-if="IS_ONEDIO && !isMobile" class="bg-white opacity-20 w-full h-px grow mx-2"></div>
      <a :href="constantData.text.headerLink" class="flex flex-row items-center" :style="{ color: constantData.style.linkColor }">
        <div class="font-normal leading-18 line-clamp-1 w-max" :class="isMobile ? 'text-md' : 'text-base'">
          {{ $t('game.all-games') }}
        </div>
        <ORawIcon icon="homepage/arrow-circle-right" svg-class="w-[18px] h-[18px] mx-1" />
      </a>
      <div class="bg-white opacity-20 ml-2 w-8 h-px"></div>
    </div>
    <div class="u-center">
      <OImage v-if="IS_ONEDIO && isMobile" loading="lazy" src="/images/game/oyunBasliyor.png" class="h-[65px] w-[151px] mt-[-20px]" />
    </div>

    <Swiper :settings="settings" :modules="[Navigation]" @swiper="onSwiper">
      <SwiperSlide v-for="(card, index) in gameList" :key="index">
        <OLink :href="slug(card)" class="game-wrapper">
          <div class="w-[150px] h-[198px] p-2 flex flex-col bg-white rounded-lg">
            <OImage loading="lazy" :src="card.thumbnail" class="w-[134px] h-[134px] rounded sm:rounded-md" />
            <OButton color-scheme="green" class="mt-2 rounded-md w-[134px] h-[40px] u-center">
              <span class="font-semibold text-base leading-22">{{ card.isPlayed ? $t('game.continue') : $t('game.play') }}</span>
            </OButton>
          </div>
        </OLink>
      </SwiperSlide>
    </Swiper>
    <ORawIcon
      v-if="!isMobile"
      ref="prevNavBtn"
      icon="homepage/arrow-circle-right"
      :style="{ color: constantData.style.arrowColor }"
      svg-class="
        absolute
        left-0
        top-1/2
        -translate-y-1/2 -translate-x-1/2
        u-center
        w-12
        h-12
        rounded-full
        z-1
        cursor-pointer
        border-white border-3
        bg-white
        rotate-180
        text-xl"
    />
    <ORawIcon
      v-if="!isMobile"
      ref="nextNavBtn"
      icon="homepage/arrow-circle-right"
      :style="{ color: constantData.style.arrowColor }"
      svg-class="
        absolute
        right-0
        top-1/2
        -translate-y-1/2
        translate-x-1/2
        u-center
        cursor-pointer
        w-12
        h-12
        rounded-full
        border-white border-3
        bg-white
        z-1
        text-xl"
    />
  </section>
</template>

<script>
import { defineComponent, computed, ref, onMounted, onBeforeMount } from '@nuxtjs/composition-api';
import { Navigation } from 'swiper';
import Swiper from '@/modules/sliders/core/Swiper';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import { OButton, OLink, OImage, OIcon, ORawIcon } from '~/modules/common/components/ui';
import { useDevice } from '~/composables/useDevice';
import { IS_ONEDIO, GAMES_PATH, LANG } from '~/constants/config';
import gamesData from '~/modules/oyun/gameData';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import lazyBackground from '~/plugins/lazyload-background';
import { onedioEvents } from '~/modules/tracking/scripts/onedioEvents';

export default defineComponent({
  props: {
    data: { type: Object },
  },
  components: { Swiper, SwiperSlide, OLink, OButton, OImage, OIcon, ORawIcon },
  directives: { lazyBackground },
  setup(props) {
    const { isMobile } = useDevice();
    const swiperRef = ref(null);
    const nextNavBtn = ref(null);
    const prevNavBtn = ref(null);
    const { sendWidgetEvent } = useTracking();
    const { sendHomepageEvent } = onedioEvents();
    const impressionTimeout = ref(null);
    const games = gamesData.filter((game) => game.description[LANG] && game.source !== 'gamedistribution');
    const gameList = ref(games);
    const getGameStatus = (slug) => {
      return localStore.getItem(slug);
    };
    onMounted(() => {
      for (let i = 0; i < (isMobile.value ? 2 : 6); i++) {
        sendWidgetEvent('view', 'Oyun', gameList.value[i].name, i + 1);
      }
    });

    onBeforeMount(() => {
      for (let i = 0; i < gameList.value.length; i++) {
        const isPlayed = getGameStatus(gameList.value[i].slug);
        gameList.value[i].isPlayed = isPlayed;
      }
    });

    const onSwiper = (swiper) => {
      swiperRef.value = swiper;
      swiper.on('slideChange', function () {
        clearTimeout(impressionTimeout.value);
        impressionTimeout.value = setTimeout(() => {
          const slideIndex = swiper.realIndex + (isMobile.value ? 1 : 0);
          for (let i = slideIndex; i < (isMobile.value ? slideIndex + 1 : slideIndex + 6); i++) {
            sendWidgetEvent('view', 'Oyun', gameList.value[i].name, i + 1);
          }
        }, 1000);
      });
      swiper.on('click', function () {
        const slideIndex = parseInt(swiper.clickedSlide.dataset.swiperSlideIndex);
        sendHomepageEvent('Oyunlar', 'click', gameList.value[slideIndex].name);
      });
    };
    const slideChange = (index) => {
      swiperRef.value?.slideToLoop(index);
    };
    const slug = (card) => {
      if (card.source === 'onedio') return '/' + card.slug;
      return constantData.text.headerLink + '/' + card.slug;
    };
    // Temporary data. When data will come from backend, these constants will be changed
    const constantData = {
      style: {
        icon: 'game',
        arrowColor: '#080C25',
        linkColor: '#A5B2EC',
        headerTextColor: '#FFC107',
      },
      text: {
        headerLink: `/${GAMES_PATH}`,
      },
    };

    const settings = computed(() => ({
      slidesPerView: isMobile.value ? 2.4 : 6,
      slidesPerGroup: isMobile.value ? 1 : 6,
      speed: 150,
      loop: true,
      preloadImages: false,
      navigation: {
        nextEl: nextNavBtn,
        prevEl: prevNavBtn,
      },
      breakpoints: {
        320: { spaceBetween: 16 },
        640: { spaceBetween: 24 },
      },
    }));
    return {
      settings,
      nextNavBtn,
      prevNavBtn,
      onSwiper,
      constantData,
      slideChange,
      Navigation,
      isMobile,
      gameList,
      IS_ONEDIO,
      slug,
    };
  },
});
</script>

<style scoped lang="scss">
.game-wrapper {
  display: block;
  color: #ffffff;
  transition: all 0.2s 0s ease-in;
  &:hover {
    transform: scale(0.95);
  }
}
.widget-line {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.22);
}
.content-visibility {
  content-visibility: auto;
  contain-intrinsic-size: 350px;
  @include sm() {
    content-visibility: visible;
    contain-intrinsic-size: 348px;
  }
}
</style>
